
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import ApiService from '@/common/api.service';
import { getToken } from '@/common/jwt.service';
import { API_URL } from '@/common/config';
import { useSessionsStore } from './sessions';

export const useResourceStore = defineStore('resource', () => {
  // State
  const resources = ref(null);
  const resourceList = ref([]);

  // Getters
  const getResourceById = computed(() => (id) => resourceList.value.find(
    (resource) => resource.id === id,
  ));
  const getResourceByName = computed(() => (name) => resourceList.value.find(
    (resource) => resource.name === name,
  ));

  // Actions
  function computeFolder(currentFolder) {
    if (!currentFolder) return 0;
    currentFolder.resources = Array.isArray(currentFolder.resources) ? currentFolder.resources : [];
    currentFolder.subFolders = Array.isArray(currentFolder.subFolders) ? currentFolder.subFolders : [];

    const unViewedResources = currentFolder.resources
      .filter((r) => r.watched === false && r.available === true);
    currentFolder.unviewed = unViewedResources.length;
    let hierarchyUnViewedResources = unViewedResources.length;
    currentFolder.subFolders
      .filter((f) => f.available === true)
      .forEach((f) => {
        hierarchyUnViewedResources += computeFolder(f);
      });
    currentFolder.totalUnViewedResources = hierarchyUnViewedResources;
    return hierarchyUnViewedResources;
  }

  function extractResources(currentFolder) {
    if (!currentFolder) return;
    currentFolder.resources = Array.isArray(currentFolder.resources) ? currentFolder.resources : [];
    currentFolder.subFolders = Array.isArray(currentFolder.subFolders) ? currentFolder.subFolders : [];

    currentFolder.resources.forEach((r) => resourceList.value.push(r));
    currentFolder.subFolders.forEach((f) => {
      extractResources(f);
    });
  }

  function setResources(payload = {}) {
    const data = payload.data || payload;
    const res = data?.resources || null;
    computeFolder(res);
    resourceList.value = [];
    extractResources(res);
    resources.value = res;
  }

  function setResourceViewed(resourceId) {
    const resource = resourceList.value.find((f) => f.id === resourceId);
    if (resource && !resource.watched) {
      resource.watched = true;
      if (resources.value) {
        computeFolder(resources.value);
      }
    }
  }

  async function fetchResources() {
    const sessionsStore = useSessionsStore();
    const { currentSession } = sessionsStore;
    if (!currentSession) throw new Error("Current session not available");

    const { data } = await ApiService.query(`front/v1/${currentSession.id}/resources`, {});
    setResources(data);
    return data;
  }

  async function fetchFile(params = {}) {
    const { session, resourceId } = params;
    setResourceViewed(resourceId);
    const data = await ApiService.query(`front/v1/resources/download/${session}/${resourceId}`, {});
    const fileLink = document.createElement('a');
    fileLink.href = data.data;
    fileLink.setAttribute('download', '');
    document.body.appendChild(fileLink);
    fileLink.click();
    document.body.removeChild(fileLink);
  }

  async function fileContent(params = {}) {
    const { session, resourceId, fileType } = params;
    if (['SHAREPOINT_PPT', 'SHAREPOINT_EXCEL', 'SHAREPOINT_WORD'].includes(fileType)) {
      return `${API_URL}/api/front/v1/resources/download/${session}/${resourceId}?token=${getToken()}`;
    }
    const data = await ApiService.query(`front/v1/resources/download/${session}/${resourceId}`);
    setResourceViewed(resourceId);
    return data.data;
  }

  return {
    resources,
    resourceList,
    getResourceById,
    getResourceByName,
    setResources,
    fetchResources,
    fetchFile,
    fileContent,
  };
});
